/**
 * 線区マスタ (`routes-master.json`) のフェッチ。
 *
 * - WebApp 内に同梱した routes-master.json を `/data/routes-master.json` 経由で読み込む。
 * - SettingBucket からダウンロードする運用も可能だが、フロントエンドからは
 *   CloudFront 配下の同居アセットとして配信した方がシンプル。
 */

export interface RouteDirection {
  direction_name: string;
  /**
   * 方向 ID。`routes-master.json` の各方面に明示された `direction_id` フィールド
   * (例: `"0"` / `"1"`) に対応する。GTFS-RT TripUpdate の `trip.directionId` と
   * 一致させる必要があり、Transformer 側の解釈が他路線と逆の路線
   * (例: 中総緩は 0 が三鷹、1 が千葉) は本フィールドで明示する。
   *
   * 後方互換のため optional。未指定の古いデータは配列順 (index 0 = "0",
   * index 1 = "1") にフォールバックする。
   */
  direction_id?: string;
}

export interface RouteMaster {
  route_id: string;
  route_name: string;
  /**
   * 路線カラー (6桁 16 進、`#` なし)。例: `"00B2E5"`。
   * routes-master.json に定義されている表示用の路線色。省略時はフォールバック色を使う。
   */
  route_color?: string;
  /**
   * 路線カラー上に載せる文字色 (6桁 16 進、`#` なし)。例: `"FFFFFF"`。
   */
  route_text_color?: string;
  directions: RouteDirection[];
}

export interface RoutesMasterFile {
  routes: RouteMaster[];
}

/** routes-master.json を fetch して返す。 */
export async function fetchRoutesMaster(): Promise<RoutesMasterFile> {
  const res = await fetch('/data/routes-master.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to fetch routes-master.json: HTTP ${res.status}`);
  }
  return (await res.json()) as RoutesMasterFile;
}

/** 線区 + 方面 を `${route_id}#${direction_name}` 形式の routeDirection に変換 */
export function toRouteDirection(routeId: string, directionName: string): string {
  return `${routeId}#${directionName}`;
}

/** routeDirection 文字列を線区/方面に分解する。`#` が含まれない場合は両方空。 */
export function parseRouteDirection(value: string): { routeId: string; directionName: string } {
  const idx = value.indexOf('#');
  if (idx < 0) return { routeId: value, directionName: '' };
  return { routeId: value.slice(0, idx), directionName: value.slice(idx + 1) };
}

/** getRouteColor の戻り値。CSS で使える `#` 付きの背景色・文字色を返す。 */
export interface RouteColor {
  /** 背景色 (`#RRGGBB`) */
  background: string;
  /** 文字色 (`#RRGGBB`) */
  text: string;
}

/** route_color が未設定のときに使うフォールバック色 (グレー系)。 */
const FALLBACK_ROUTE_COLOR: RouteColor = { background: '#6b7280', text: '#ffffff' };

/** 6桁 16 進カラー (`#` あり/なし両対応) を `#RRGGBB` に正規化する。不正値は null。 */
function normalizeHexColor(value: string | undefined): string | null {
  if (typeof value !== 'string') return null;
  const hex = value.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return `#${hex}`;
}

/**
 * route_id に対応する路線カラーを返す (接続線タブの着色などに使う)。
 * - `routes-master.json` の `route_color` / `route_text_color` を `#RRGGBB` に正規化して返す。
 * - route_id がマスタに無い / route_color が不正な場合はフォールバック色 (グレー) を返す。
 */
export function getRouteColor(
  routes: readonly RouteMaster[] | undefined,
  routeId: string,
): RouteColor {
  const route = (routes ?? []).find((r) => r.route_id === routeId);
  const background = normalizeHexColor(route?.route_color);
  if (!background) return FALLBACK_ROUTE_COLOR;
  const text = normalizeHexColor(route?.route_text_color) ?? '#ffffff';
  return { background, text };
}
